import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik as FormikBase, Field } from "formik";
import { Navigate, Link } from "react-router-dom";
import { Button, Typography } from "@mui/material";

import { passwordMDInputFormik } from "@/helpers/formInputs";
import { isLoggedInSelector } from "@/features/auth/store/authSlice";
import {
  validatePwdResetRequestThunk,
  resetPasswordThunk,
} from "@/features/auth/store/resetPasswordSlice";
import {
  isPasswordUpdatedSelector,
  isValidRequestSelector,
  isErrorSelector,
  isLoadingSelector,
  validatedEmailSelector,
} from "@/features/auth/store/resetPasswordSlice";
import useQuery from "@/helpers/hooks/useQuery";
import LeftView from "@/features/auth/components/LeftView";
import { validSecurePassConfPass } from "@/helpers/validations";
import MESSAGE from "@/helpers/messages";
import LoadingButton from "@/components/loaders/LoadingButton";
import { encodePassword, getDashboardPath } from "@/helpers/utils";

const Formik = FormikBase as unknown as React.ComponentType<any>;

const ResetPassword = () => {
  const dispatch = useDispatch() as any;
  const isLoggedIn = useSelector(isLoggedInSelector);
  const isLoading = useSelector(isLoadingSelector);
  const isError = useSelector(isErrorSelector);
  const isValidRequest = useSelector(isValidRequestSelector);
  const isPasswordUpdated = useSelector(isPasswordUpdatedSelector);
  const validatedEmail = useSelector(validatedEmailSelector);
  const query = useQuery();

  /**
   * @method onSubmitForm
   * @description handle on submit form
   * @param {*} values
   */
  const onSubmitForm = (values: any, { setSubmitting }: any) => {
    const data = {
      token: query.token,
      email: validatedEmail,
      password: encodePassword(values.password),
      confirmPassword: encodePassword(values.confirmPassword),
    };
    dispatch(resetPasswordThunk(data));
    setSubmitting(false);
  };

  useEffect(() => {
    dispatch(validatePwdResetRequestThunk(String(query.token || "")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // redirect to home when already logged in
  if (isLoggedIn) {
    return <Navigate to={getDashboardPath()} replace />;
  }

  const renderValidatingOrErrorView = () => {
    return (
      <>
        {" "}
        <h4 className="heading8 text-center">
          {isError ? String(isError) : "Validating your request..."}
        </h4>
        {isError && (
          <div className="d-flex justify-content-center my-4">
            <Button
              component={Link}
              variant="contained"
              color="primary"
              className="px-4"
              to={"/"}
            >
              {" "}
              Log In{" "}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderPasswordUpdatedView = () => {
    return (
      <>
        <h4 className="heading8 text-center">{MESSAGE.passwordResetSuccess}</h4>
        <div className="d-flex justify-content-center my-4">
          <Button
            component={Link}
            variant="contained"
            color="primary"
            className="px-5"
            to={"/"}
          >
            {" "}
            Log In{" "}
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="row login-page">
      <div className="col-lg-6 p-0 d-none d-lg-block">
        <LeftView />
      </div>
      <div className="col-lg-6 d-flex flex-column justify-content-center px-lg-5 py-sm-5">
        <div className="login-form">
          {!isValidRequest ? (
            renderValidatingOrErrorView()
          ) : isPasswordUpdated ? (
            renderPasswordUpdatedView()
          ) : (
            <>
              <Typography
                component="h2"
                variant="h4"
                className="fs-39"
                gutterBottom
              >
                Reset Password
              </Typography>
              <Formik
                initialValues={{ password: "", confirmPassword: "" }}
                onSubmit={onSubmitForm}
                validate={validSecurePassConfPass}
              >
                {({ handleSubmit }: any) => {
                  return (
                    <form onSubmit={handleSubmit} className="mt-4 mt-md-5">
                      <Field
                        name="password"
                        label="Password"
                        component={passwordMDInputFormik}
                        autoFocus={true}
                      />

                      <Field
                        name="confirmPassword"
                        label="Confirm Password"
                        component={passwordMDInputFormik}
                      />

                      <div className="form-group mt-2 d-flex justify-content-end">
                        <LoadingButton
                          type="submit"
                          className="primary"
                          text="Reset Password"
                          isLoading={isLoading}
                        />
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
